import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useSubmitFieldsTable } from '@/modules/AiAgents/apiHooks';
import { FieldsAdditionSuccessModal } from '@/modules/AiAgents/components/Agents/Nugz/Modals/FieldsAdditionSuccessModal';
import { AIContext } from '@/modules/AiAgents/context/nugzContext/context';
import { AIActionsTypes } from '@/modules/AiAgents/context/nugzContext/reducer';
import { Typography } from '@/shared/components/atoms';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const SubmitButton = () => {
  const { t } = useTranslation('common');
  const {
    dispatch,
    state: { tableList, isLoading, isEditing },
  } = useContext(AIContext);
  const { openModal } = useModal();
  const [selectedAsset] = useStore((store) => store?.asset);
  const { mutate, isLoading: isSubmitLoading } = useSubmitFieldsTable();
  const { handleSubmit } = useFormContext();
  const queryClient = useQueryClient();

  // Check if any field has duplicates
  const hasDuplicates = tableList.some(
    (field) => field.hasExistingDuplicate || field.hasSessionDuplicate
  );

  const onSubmit = () => {
    if (selectedAsset) {
      mutate(
        {
          entityId: selectedAsset.id,
          data: {
            fields: tableList,
          },
        },
        {
          onSuccess: () => {
            openModal(
              <FieldsAdditionSuccessModal numberOfFields={tableList.length} />,
              {
                containerClassName: 'min-w-[30vw]',
              }
            );
            // invalidate this query ["getPropertyFields",{"isArchived":false,"pageNumber":1,"pageSize":1000,"propertyId":581}]
            const queryKey = [
              'getPropertyFields',
              {
                isArchived: false,
                pageNumber: 1,
                pageSize: 1000,
                propertyId: selectedAsset?.id,
              },
            ];
            queryClient.invalidateQueries(queryKey);
          },
        }
      );
    }
  };

  useEffect(() => {
    dispatch({
      type: AIActionsTypes.setIsLoading,
      payload: isSubmitLoading,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitLoading]);

  return (
    <div className='flex w-11/12 items-center justify-between gap-16 px-16 pt-24 ltr:mr-auto rtl:ml-auto desktop:pt-16'>
      <Typography variant='caption' className='text-surface-10'>
        {t('review-ai-data')}
      </Typography>
      <button
        type='button'
        disabled={isEditing || isLoading || hasDuplicates}
        className={clsx(
          isEditing || isLoading || hasDuplicates
            ? 'cursor-not-allowed opacity-5'
            : 'cursor-pointer',
          'rounded-xl bg-primary-60 px-16 py-2 text-base font-semi-bold text-white'
        )}
        onClick={handleSubmit(onSubmit)}
      >
        {t('submit')}
      </button>
    </div>
  );
};

export { SubmitButton };
